import { FC, useEffect, useState } from "react";
import { Modal, Form, Select, DatePicker, InputNumber, Input, Button, Switch, Divider, Tag } from "antd";
import dayjs from "dayjs";
import { MapPin, Package } from "lucide-react";
import { useEstimateDetailStore } from "./estimate-detail.store";
import { useAddEstimateService, useUpdateEstimateService } from "~/services/estimate";
import { ServiceType } from "~/utils/constants";
import { useNotification } from "~/hooks";
import { useGetClient } from "~/services/client";
import { useGetActiveInventoryTemplates } from "~/services/inventory-templates/inventory-templates.queries";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { formatFullAddress } from "~/utils";

const { TextArea } = Input;

export const ServiceFormModal: FC = () => {
  const {
    showServiceForm,
    closeServiceForm,
    estimate,
    selectedService,
    isAddingNewService,
  } = useEstimateDetailStore();

  const [form] = Form.useForm();
  const { success, error } = useNotification();
  const [useQuickLocation, setUseQuickLocation] = useState(false);

  // Fetch client to get shipping addresses (only if clientId exists)
  const { data: client } = useGetClient(estimate?.clientId);

  // Fetch active inventory templates
  const { data: inventoryTemplates } = useGetActiveInventoryTemplates();

  // Fetch vessels
  const { data: vessels } = useGetVesselsForDropdown();

  const addServiceMutation = useAddEstimateService();
  const updateServiceMutation = useUpdateEstimateService();

  useEffect(() => {
    if (showServiceForm && selectedService && !isAddingNewService) {
      // Editing existing service
      form.setFieldsValue({
        serviceType: selectedService.serviceType,
        serviceDate: selectedService.serviceDate ? dayjs(selectedService.serviceDate) : undefined,
        duration: selectedService.duration,
        staffCount: selectedService.staffCount,
        vesselId: selectedService.vesselId,
        locationId: selectedService.locationId,
        quickCity: selectedService.tempLocationData?.city,
        quickZip: selectedService.tempLocationData?.zip,
        inventoryTemplateIds: selectedService.inventoryTemplates?.map(t => t.id) || [],
        notes: selectedService.notes,
        estimatedItemCount: selectedService.estimatedItemCount,
      });

      // Set quick location mode if temp data exists
      if (selectedService.tempLocationData) {
        setUseQuickLocation(true);
      }
    } else if (showServiceForm && isAddingNewService) {
      // Adding new service
      form.resetFields();
      setUseQuickLocation(false);
    }
  }, [showServiceForm, selectedService, isAddingNewService, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload: any = {
        estimateTicketId: estimate!.id,
        serviceType: values.serviceType,
        serviceDate: values.serviceDate ? values.serviceDate.toISOString() : undefined,
        duration: values.duration,
        staffCount: values.staffCount,
        vesselId: values.vesselId,
        inventoryTemplateIds: values.inventoryTemplateIds || [],
        notes: values.notes,
        estimatedItemCount: values.estimatedItemCount,
      };

      // Add location data
      if (useQuickLocation) {
        payload.tempLocationData = {
          city: values.quickCity,
          zip: values.quickZip,
        };
      } else if (values.locationId) {
        payload.locationId = values.locationId;
      }

      if (isAddingNewService) {
        // Add new service
        await addServiceMutation.mutateAsync(payload);
        success({ message: "Service added successfully!" });
      } else {
        // Update existing service
        const updatePayload = { ...payload };
        delete updatePayload.estimateTicketId; // Remove this for updates

        await updateServiceMutation.mutateAsync({
          serviceId: selectedService!.id,
          estimateId: estimate!.id,
          data: updatePayload,
        });
        success({ message: "Service updated successfully!" });
      }

      closeServiceForm();
      form.resetFields();
      setUseQuickLocation(false);
    } catch (err: any) {
      console.error("Service form error:", err);

      if (err.errorFields) {
        // Form validation error
        error({ message: "Please fill in all required fields" });
        return;
      }

      // API error
      const errorMessage = err.response?.data?.message || err.message || "Failed to save service";
      console.error("API Error:", errorMessage);
      error({ message: errorMessage });
    }
  };

  const serviceTypeOptions = [
    { value: ServiceType.COLLECTION, label: "Collection" },
    { value: ServiceType.DELIVERY, label: "Delivery" },
    { value: ServiceType.WAREHOUSE_SERVICE, label: "Warehouse Service" },
    { value: ServiceType.FABRICATION_SERVICE, label: "Fabrication Service" },
    { value: ServiceType.ON_SITE_SERVICE, label: "On-Site Service" },
  ];

  const vesselOptions = (vessels || []).map((vessel) => ({
    value: vessel.id,
    label: `${vessel.name}${vessel.hourlyRate ? ` ($${vessel.hourlyRate}/hr)` : ''}`,
  }));

  return (
    <Modal
      title={isAddingNewService ? "Add Service to Estimate" : "Edit Service"}
      open={showServiceForm}
      onCancel={() => {
        closeServiceForm();
        setUseQuickLocation(false);
      }}
      width={800}
      footer={[
        <Button key="cancel" onClick={() => {
          closeServiceForm();
          setUseQuickLocation(false);
        }}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={addServiceMutation.isPending || updateServiceMutation.isPending}
          onClick={handleSubmit}
        >
          {isAddingNewService ? "Add Service" : "Save Changes"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" className="mt-4">
        {/* Service Type */}
        <Form.Item
          label="Service Type"
          name="serviceType"
          rules={[{ required: true, message: "Please select a service type" }]}
        >
          <Select
            placeholder="Select service type"
            options={serviceTypeOptions}
            size="large"
          />
        </Form.Item>

        {/* Date and Duration */}
        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Service Date" name="serviceDate">
            <DatePicker
              className="w-full"
              size="large"
              format="MMM DD, YYYY"
            />
          </Form.Item>

          <Form.Item
            label="Duration (hours)"
            name="duration"
            tooltip="Estimated time to complete this service"
          >
            <InputNumber
              className="w-full"
              size="large"
              min={0}
              step={0.5}
              placeholder="e.g. 4"
            />
          </Form.Item>
        </div>

        {/* Staff and Vehicle */}
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Staff Count"
            name="staffCount"
            tooltip="Number of staff members needed"
          >
            <InputNumber
              className="w-full"
              size="large"
              min={1}
              placeholder="e.g. 2"
            />
          </Form.Item>

          <Form.Item label="Vessel/Truck" name="vesselId">
            <Select
              placeholder="Select vessel/truck"
              options={vesselOptions}
              size="large"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>

        <Divider orientation="left">
          <MapPin size={16} className="inline mr-2" />
          Location
        </Divider>

        {/* Location Selection */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Use Quick Location?</span>
            <Switch
              checked={useQuickLocation}
              onChange={setUseQuickLocation}
              checkedChildren="City + Zip"
              unCheckedChildren="Full Address"
            />
          </div>

          {useQuickLocation ? (
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  label="City"
                  name="quickCity"
                  className="mb-0"
                  rules={[{ required: useQuickLocation, message: "Required" }]}
                >
                  <Input placeholder="e.g. Brooklyn" size="large" />
                </Form.Item>
                <Form.Item
                  label="Zip Code"
                  name="quickZip"
                  className="mb-0"
                  rules={[{ required: useQuickLocation, message: "Required" }]}
                >
                  <Input placeholder="e.g. 11201" size="large" />
                </Form.Item>
              </div>
            </div>
          ) : (
            <Form.Item
              label="Select Address"
              name="locationId"
              rules={[{ required: !useQuickLocation, message: "Please select an address" }]}
            >
              <Select
                placeholder="Select shipping address"
                size="large"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={client?.shippingAddresses?.map((addr: any) => ({
                  value: addr.id,
                  label: formatFullAddress(addr),
                }))}
              />
            </Form.Item>
          )}
        </div>

        <Divider orientation="left">
          <Package size={16} className="inline mr-2" />
          Inventory & Details
        </Divider>

        {/* Item Count */}
        <Form.Item
          label="Estimated Item Count"
          name="estimatedItemCount"
          tooltip="Approximate number of items to be handled"
        >
          <InputNumber
            className="w-full"
            size="large"
            min={0}
            placeholder="e.g. 25"
          />
        </Form.Item>

        {/* Inventory Templates Selection */}
        <Form.Item
          label="Inventory Templates"
          name="inventoryTemplateIds"
          tooltip="Select pre-configured inventory templates for this service"
        >
          <Select
            mode="multiple"
            placeholder="Select inventory templates"
            size="large"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={inventoryTemplates?.map((template) => ({
              value: template.id,
              label: `${template.name}${template.description ? ` - ${template.description}` : ""}`,
            }))}
            tagRender={({ label, value, closable, onClose }) => {
              const template = inventoryTemplates?.find(t => t.id === value);
              return (
                <Tag
                  color="blue"
                  closable={closable}
                  onClose={onClose}
                  className="mr-1 mb-1"
                >
                  {template?.name || label}
                  {template?.length && template?.width && template?.height && (
                    <span className="ml-1 text-xs opacity-70">
                      ({template.length}×{template.width}×{template.height})
                    </span>
                  )}
                </Tag>
              );
            }}
          />
        </Form.Item>

        {/* Pricing Info */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
          <div className="text-sm text-blue-800">
            <strong>Pricing Calculation:</strong> Service price will be automatically calculated based on
            Duration × (Staff + Vessel rates) + Packing Materials
          </div>
        </div>

        {/* Notes */}
        <Form.Item label="Notes" name="notes">
          <TextArea
            rows={4}
            placeholder="Add any notes about this service..."
            size="large"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
